import { UNIT_MAPPINGS, DATE_FORMAT_OPTIONS, HIDDEN_OBS_VARIABLES, EXCLUDED_OBS_VARIABLE_SUFFIXES, EXCLUDED_OBS_VARIABLES, categories } from "./constants";
import { isMobileSafari, isAndroid, isIOS } from 'mobile-device-detect';
import Qty from 'js-quantities/esm';
import { canonicalUnitsToJsQty, parameterPreferredUnits } from './constants';

// Converts timestamp from ISO 8601 to a user friendly format
export const calculateTimeDifferenceString = (timestamp) => {
  const currentDate = new Date();
  const timeDifferenceInSeconds = Math.floor((currentDate - new Date(timestamp)) / 1000);
  const daysDifference = Math.floor(timeDifferenceInSeconds / 86400);
  const hoursDifference = Math.floor((timeDifferenceInSeconds % 86400) / 3600);
  const minutesDifference = Math.floor((timeDifferenceInSeconds % 3600) / 60);

  let timeDifferenceString = '';

  if (daysDifference > 0) {
    const daysText = daysDifference === 1 ? 'day' : 'days';
    timeDifferenceString += `${daysDifference} ${daysText}`;
  }

  if (hoursDifference > 0) {
    const hoursText = hoursDifference === 1 ? 'hour' : 'hours';
    timeDifferenceString += `${timeDifferenceString === '' ? '' : ', '} ${hoursDifference} ${hoursText}`;
  }

  if (minutesDifference > 0) {
    const minutesText = minutesDifference === 1 ? 'minute' : 'minutes';
    timeDifferenceString += `${timeDifferenceString === '' ? '' : ', '} ${minutesDifference} ${minutesText}`;
  }

  return `${timeDifferenceString} ago`;
};

/**
 * In an array of observation objects with a time property, find the observation closest to current time, but not in the future.
 * Optionally for a specific parameter. If no parameter is provided the most recent observation across 
 * all parameters will be returned.
 * 
 * @param {array} observations 
 * @param {string} [parameter] - optional parameter standard name
 * @returns 
 */
export const getLatestObservation = (observations, parameter = null) => {
  if (!observations) return null;

  const currentTime = new Date();

  const closestTimeObject = observations.reduce((closest, current) => {
    const objTime = new Date(current.time);
    const timeDifference = Math.abs(currentTime - objTime);

    if (
      timeDifference < closest.timeDifference
      && objTime <= currentTime
      && ((parameter && current[parameter]) || !parameter)
    ) {
      return { timeDifference, current };
    }

    return closest;
  }, { timeDifference: Infinity, current: null });

  return closestTimeObject.current;
};

/**
 * Get the two most recent data points for a specific parameter from an array of observations.
 * This is needed since on some stations, parameters report at different intervals.
 * i.e. air_temperature may be updated every 5 minutes, but sea_surface_wave_directional_spread only every hour. 
 * This leads to some timestamps containing null values for some parameters.
 * @param {array} observations 
 * @param {string} parameter 
 * @returns 
 */
const getLastTwoDataPoints = (observations, parameter) => {
  // Sort the observations by time in ascending order
  const sortedObservations = observations.sort((a, b) => new Date(a.time) - new Date(b.time));
  const nonNullDataPoints = sortedObservations.filter(obj => obj[parameter]);

  if (nonNullDataPoints.length < 2) {
    // Not enough valid data points
    return [];
  }

  return nonNullDataPoints.slice(-2);
};

/**
 * Get the trend for an array of observations. 
 * Returns 'increasing'||'decreasing'||'stable' or null if not enough data to calculate.
 * @param {array} observations 
 * @param {string} parameter 
 * @returns {string}
 */
export const getParameterTrend = (observations, parameter) => {
  if (!observations || observations?.length < 2 || !parameter) return null;

  const lastTwoDataPoints = getLastTwoDataPoints(observations, parameter);
  if (lastTwoDataPoints.length !== 2) {
    return null;
  };
  const mostRecentValue = lastTwoDataPoints[1][parameter];
  const secondMostRecentValue = lastTwoDataPoints[0][parameter];

  if (mostRecentValue > secondMostRecentValue) {
    return "increasing";
  } else if (mostRecentValue < secondMostRecentValue) {
    return "decreasing";
  }

  return "stable";
}

const displayUnit = (unit) => {
  if (UNIT_MAPPINGS.hasOwnProperty(unit)) {
    return UNIT_MAPPINGS[unit];
  }
  return unit;
}

/**
 * @param {string} parameter - standard name
 * @param {number} value - observation value
 * @param {string} unit - the current unit of the value
 * @param {string} preferredUnitSystem - 'imperial' or 'metric'
 * @returns {object} - with value and unit properties
 */
export const getObservationInPreferredUnits = (parameter, value, unit, preferredUnitSystem) => {
  if ((!value && value !== 0) || !unit || !parameter) {
    return null;
  }

  const preferredUnit = parameterPreferredUnits?.[parameter]?.[preferredUnitSystem];

  const unitToConvertFrom = canonicalUnitsToJsQty[unit] || unit;

  if (preferredUnit && unit !== preferredUnit) {
    try {
      const jsQty = Qty(value, unitToConvertFrom);
      const convertedValue = jsQty.to(preferredUnit);
      const roundedValue = convertedValue.toPrec(0.01).scalar;

      return {
        value: roundedValue,
        unit: displayUnit(preferredUnit)
      };
    } catch (error) {
      console.error(`Error converting ${value} in ${unit} to ${preferredUnit}:`, error);
    }
  }

  return {
    value: parseFloat(value.toFixed(2)),
    unit: displayUnit(unit)
  }
}

/**
 * Get the long name (or display name) for a specified parameter from its metadata.
 * Returns empty string if parameter could not be found.
 * 
 * @param {array} metadata 
 * @param {string} standardName 
 * @returns {string}
 */
export const getParameterDisplayName = (metadata, standardName) => {
  if (!metadata || metadata?.length < 1 || !standardName) return '';

  const variable = metadata.find((v) => v["Variable Name"] === standardName && v["Attribute Name"] === "long_name")
  if (!variable?.Value) {
    return '';
  }

  let variableName = variable?.Value;

  const datum = metadata.find((v) => v["Variable Name"] === standardName && v["Attribute Name"] === "vertical_datum");

  if (datum?.Value) {
    variableName += ` (${datum.Value})`;
  }

  return variableName;
};

export const getParameterCategory = (standardName) => {
  return parameterPreferredUnits?.[standardName]?.category || '';
};

export const getPreferredUnit = (standardName, unit, preferredUnitSystem) => {
  const preferredUnit = parameterPreferredUnits?.[standardName]?.[preferredUnitSystem];

  return preferredUnit ? preferredUnit : unit;
};

/**
 * Get the display unit in the preferred unit system for a specified parameter from its metadata. 
 * If a unit mapping exists, that will be used.
 * Returns empty string if parameter could not be found.
 * 
 * @param {array} metadata 
 * @param {string} standardName 
 * @param {string} preferredUnitSystem
 * @returns {string}
 */
export const getDisplayUnit = (metadata, standardName, preferredUnitSystem) => {
  const paramUnitMetadata = metadata?.find((v) => v["Variable Name"] === standardName && v["Attribute Name"] === "units")
  if (!paramUnitMetadata) {
    return '';
  }

  const rawUnit = paramUnitMetadata["Value"];
  const variableName = paramUnitMetadata["Variable Name"]

  if (preferredUnitSystem === 'imperial') {
    const paramPreferredUnit = parameterPreferredUnits[variableName].imperial;
    if (UNIT_MAPPINGS.hasOwnProperty(paramPreferredUnit)) {
      return UNIT_MAPPINGS[paramPreferredUnit];
    }
    return paramPreferredUnit;
  }

  if (UNIT_MAPPINGS.hasOwnProperty(rawUnit)) {
    return UNIT_MAPPINGS[rawUnit];
  } else {
    // If no mapping is found, return the original unitName
    return rawUnit;
  }
};

/**
 * Convert from timestamp to string describing relative time such as "2 hours ago" or "In 2 days"
 * Inspired by https://natclark.com/tutorials/javascript-relative-time/
 * TODO: support terms like "yesterday", "tomorrow", "Thursday"?
 * @param {string} date - ISO 8601 string
 * @return {string} 
 */
export const getRelativeTime = (date) => {
  // divide by 1000 to go from ms to s
  const input = Math.floor(new Date(date).getTime() / 1000);
  const now = Math.floor(new Date().getTime() / 1000);

  const second = 1;
  const minute = second * 60;
  const hour = minute * minute;
  const day = hour * 24;
  const month = day * 30.437; // average days in a month
  const year = month * 12;

  const delta = now - input;

  // handle future time
  if (delta < 1) {
    const futureDelta = Math.abs(delta);

    // TODO: clean this up, works for now

    if (futureDelta < minute) {
      // More than a minute in the future:
      const unit = futureDelta > 1 ? 'seconds' : 'second';
      return `In ${futureDelta} ${unit}`;
    } else if (futureDelta < hour) {
      // More than an hour in the future:
      const minutes = Math.floor(futureDelta / minute);
      const unit = minutes > 1 ? 'minutes' : 'minute';
      return `In ${minutes} ${unit}`;
    } else if (futureDelta < day) {
      // More than a day in the future:
      const hours = Math.floor(futureDelta / hour);
      const unit = hours > 1 ? 'hours' : 'hour';
      return `In ${hours} ${unit}`;
    } else if (futureDelta < month) {
      // More than a month in the future:
      const days = Math.floor(futureDelta / day);
      const unit = days > 1 ? 'days' : 'day';
      return `In ${days} ${unit}`;
    } else if (futureDelta < year) {
      //  More than a year in the future:
      const months = Math.floor(futureDelta / month);
      const unit = months > 1 ? 'months' : 'month';
      return `In ${months} ${unit}`;
    } else {
      // More than a year in the future:
      const years = Math.floor(futureDelta / year);
      const unit = years > 1 ? 'years' : 'year';
      return `In ${years} ${unit}`;
    }
  }

  // handle past dates
  if (delta < minute) {
    // Less than a minute has passed:
    const unit = delta > 1 ? 'seconds' : 'second';
    return `${delta} ${unit} ago`;
  } else if (delta < hour) {
    // Less than an hour has passed:
    const minutes = Math.floor(delta / minute);
    const unit = minutes > 1 ? 'minutes' : 'minute';
    return `${minutes} ${unit} ago`;
  } else if (delta < day) {
    // Less than a day has passed:
    const hours = Math.floor(delta / hour);
    const unit = hours > 1 ? 'hours' : 'hour';
    return `${hours} ${unit} ago`;
  } else if (delta < month) {
    // Less than a month has passed:
    const days = Math.floor(delta / day);
    const unit = days > 1 ? 'days' : 'day';
    return `${days} ${unit} ago`;
  } else if (delta < year) {
    //  Less than a year has passed:
    const months = Math.floor(delta / month);
    const unit = months > 1 ? 'months' : 'month';
    return `${months} ${unit} ago`;
  } else {
    // More than a year has passed:
    const years = Math.floor(delta / year);
    const unit = years > 1 ? 'years' : 'year';
    return `${years} ${unit} ago`;
  }
};

/**
 * Get a station's name from it's metadata
 * @param {array} stationMetadata 
 * @returns {string}
 */
export const getStationName = (stationMetadata) => {
  if (!stationMetadata || stationMetadata?.length < 1) return '';

  const displayName = stationMetadata.find(item => {
    return item["Variable Name"] === "station" && item["Attribute Name"] === "long_name";
  })

  if (displayName?.Value) {
    return displayName.Value;
  }

  return '';
};

/**
 * 
 * @param {string} date - ISO 8601 string
 * @returns {string}
 */
export const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString("en-US", DATE_FORMAT_OPTIONS);
};

/**
 * Get the unique variables (or, parameters) across all datasets/stations excluding variables we don't want to display in the UI
 * 
 * @param {array} datasetsMetadata - metadata for all datasets
 * @returns {object[]} array of parameter objects with standardName and displayName attributes
 */
export const getUniqueVariables = (datasetsMetadata) => {
  if (!datasetsMetadata || datasetsMetadata?.length < 1) return [];

  const uniqueVariables = [];

  datasetsMetadata.forEach((datasetMetadata) => {
    const metadata = datasetMetadata?.metadata;
    const datasetVariables = metadata?.filter((o) => o["Row Type"] === "variable");
    datasetVariables?.forEach((variable) => {
      const standardName = variable["Variable Name"];
      const displayName = getParameterDisplayName(metadata, standardName);
      const unit = getParameterUnit(metadata, standardName);
      const category = getParameterCategory(standardName);
      if (
        !uniqueVariables.find((v) => v.standardName === standardName)
        && !HIDDEN_OBS_VARIABLES.includes(standardName)
        && !EXCLUDED_OBS_VARIABLES.includes(standardName)
        && !EXCLUDED_OBS_VARIABLE_SUFFIXES.some(term => standardName.endsWith(term))
      ) {
        uniqueVariables.push({
          standardName: standardName,
          displayName: displayName,
          unit: unit,
          category: category,
        });
      }
    });
  });

  return uniqueVariables;
};

/**
 * Capitalize the first letter of each word in a string except for words like "and", "or", "the", "a
 * @param {string} str 
 * @returns {string} str with first letter of each word capitalized
 */
export const toTitleCase = (str) => {
  const exceptions = ["and", "or", "the", "a"];

  return str.toLowerCase()
    .split(' ')
    .map((s) => exceptions.includes(s) ? s : s.charAt(0).toUpperCase() + s.substring(1))
    .join(' ');
};

/**
 * Returns the user's platform type
 *
 * @returns {string} Returns the user's platform type
 */
export const getPlatformType = () => {
  if (isAndroid) {
    return 'android'
  }
  else if (window.hasOwnProperty("BeforeInstallPromptEvent")) {
    return 'native';
  }
  else if (isIOS && isMobileSafari) {
    return 'iOS';
  }
  else {
    return 'other'
  }
}

/**
 * Given a degrees value, returns cardinal direction
 * @param {number} degrees - Wind direction in degrees (0–360)
 * @returns {string} Cardinal direction - (N, NNE, NE, etc.)
 */
export const getCardinalDirection = (degrees) => {
  if (degrees === null || degrees === undefined || Number.isNaN(degrees)) {
    return '';
  }

  const directions = [
    "N",  "NNE", "NE",  "ENE",
    "E",  "ESE", "SE",  "SSE",
    "S",  "SSW", "SW",  "WSW",
    "W",  "WNW", "NW",  "NNW",
  ];

  // 360 / 16 = 22.5° per sector - round to nearest sector
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}

/**
 * Get the unit for a specified parameter from its metadata. 
 * Returns empty string if parameter could not be found.
 * 
 * @param {array} metadata 
 * @param {string} standardName 
 * @returns {string}
 */
export const getParameterUnit = (metadata, standardName) => {
  if (!metadata || metadata?.length < 1 || !standardName) return '';
  const unit = metadata.find((v) => v["Variable Name"] === standardName && v["Attribute Name"] === "units")
  if (!unit) {
    return '';
  }

  if (Object.keys(UNIT_MAPPINGS).includes((unit["Value"]))) {
    return UNIT_MAPPINGS[unit["Value"]];
  }

  return unit["Value"];
};

/**
 * Handles region selection filtering
 * @param {string} region
 * @param {array} selectedRegions
 * @param {function} setSelectedRegions
 * @returns {void} 
 */
export const toggleSelectedRegion = (region, selectedRegions, setSelectedRegions) => {
  if (region.length === 0) {
    setSelectedRegions([]);
  } else {
    if (selectedRegions.includes(region)) {
      // filter region out of selectedRegions
      setSelectedRegions(selectedRegions.filter((r) => (r) !== region));
    } else {
      // add region to selectedRegions
      setSelectedRegions([...selectedRegions, region]);
    }
  }
};

export const filterTidalBuoys = (allStations) => {
  return allStations?.filter((station) => !station?.isTidalBuoy) ?? [];
}

/**
 * Gets the region name when provided a region Id
 * @param {string} regionId
 * @param {array} regions
 * @returns {string} 
 */
export const getRegionName = (regionId, regions) => {
    const region = regions.find((region) => region?.id === regionId);

    if (region) {
      return region.name;
    }

    return null;
}

export const getParametersByCategory = () => {
  const categoriesWithParams = Object.keys(categories).map((cat) => {
    return {
      label: categories[cat],
      parameters: Object.entries(parameterPreferredUnits).filter(([param, meta]) => meta.category === categories[cat])
    }
  });

  return categoriesWithParams;
}
